import i18next, { translate as translateKey } from './config';

// 翻譯參數類型
interface TranslationParams {
    [key: string]: string | number | boolean | unknown;
}

// DOM 元素類型（含 <template>.content 的 DocumentFragment）
type DOMElement = Element | Document | DocumentFragment;

class Translator {
    // 更新所有需要翻譯的元素
    static updateElements(container: DOMElement = document): void {
        // <template> 內容不在 DOM 樹裡，一起翻譯，之後 clone 出來的列就直接是目前語系
        container.querySelectorAll('template').forEach(template => {
            this.updateElements(template.content);
        });

        // 選擇所有帶有 data-i18n 或 data-i18n-placeholder 屬性的元素
        container
            .querySelectorAll('[data-i18n], [data-i18n-placeholder]')
            .forEach((element: Element) => {
                let params: TranslationParams = {};

                // 解析參數
                const paramsAttr = element.getAttribute('data-i18n-params');
                if (paramsAttr) {
                    try {
                        params = JSON.parse(paramsAttr) as TranslationParams;
                    } catch (e) {
                        console.error('Invalid data-i18n-params:', paramsAttr, e);
                    }
                }

                // 處理 data-i18n 屬性
                if (element.hasAttribute('data-i18n')) {
                    const key = element.getAttribute('data-i18n');
                    if (key) {
                        // 使用支持嵌套命名空間的 translate 函數
                        const translatedText = translateKey(key, params);

                        // 檢查是否需要使用 innerHTML（支持 HTML 標籤）
                        const useHTML = element.hasAttribute('data-i18n-html');

                        if (useHTML) {
                            element.innerHTML = translatedText;
                        } else {
                            element.textContent = translatedText;
                        }
                    }
                }

                // 處理 data-i18n-placeholder 屬性
                if (element.hasAttribute('data-i18n-placeholder')) {
                    const placeholderKey = element.getAttribute('data-i18n-placeholder');
                    if (placeholderKey) {
                        // 使用支持嵌套命名空間的 translate 函數
                        element.setAttribute('placeholder', translateKey(placeholderKey, params));
                    }
                }
            });
    }

    // 更新頁面標題
    static updatePageTitle(titleKey: string, params: TranslationParams = {}): void {
        // 使用支持嵌套命名空間的 translate 函數
        document.title = translateKey(titleKey, params);
    }

    // 切換語言
    static async changeLanguage(lang: string): Promise<string> {
        await i18next.changeLanguage(lang);
        this.updateElements();
        return lang;
    }

    // 獲取當前語言
    static getCurrentLanguage(): string {
        return i18next.language;
    }

    // 翻譯單個文字
    static translate(key: string, params: TranslationParams = {}): string {
        // 使用支持嵌套命名空間的 translate 函數
        return translateKey(key, params);
    }

    // 檢查語言是否可用
    static isLanguageAvailable(lang: string): boolean {
        return i18next.hasResourceBundle(lang, 'common');
    }

    // 獲取所有可用語言
    static getAvailableLanguages(): readonly string[] {
        return i18next.languages;
    }

    // 獲取翻譯鍵值是否存在
    static hasTranslation(key: string): boolean {
        return i18next.exists(key);
    }

    // 批量翻譯多個鍵值
    static translateMultiple(
        keys: string[],
        params: TranslationParams = {}
    ): Record<string, string> {
        const result: Record<string, string> = {};
        keys.forEach(key => {
            // 使用支持嵌套命名空間的 translate 函數
            result[key] = translateKey(key, params);
        });
        return result;
    }
}

export default Translator;
export type { TranslationParams };
